import type { JssStyle } from 'jss';
import { color as colorUtils } from '../variables/color';

export const IMAGE_STYLE = ['default', 'circle'];
export type TImageStyle = (typeof IMAGE_STYLE)[number];

export const IMAGE_OBJECT_FIT = ['fill', 'contain', 'cover', 'none', 'scale-down'];
export type TImageObjectFit = (typeof IMAGE_OBJECT_FIT)[number];

export const pxToRemWithUnit = (px: number): string => `${px / 16}rem`;

/**
 * Screen reader only styles to hide (text-)contents visually in the browser but grant access for screen readers
 */
export const getScreenReaderJssStyle = (isHidden: boolean): JssStyle =>
  isHidden
    ? {
        position: 'absolute',
        width: '1px',
        height: '1px',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(1px,1px,1px,1px)',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
      }
    : {
        position: 'static',
        width: 'auto',
        height: 'auto',
        margin: 0,
        overflow: 'visible',
        clip: 'auto',
        clipPath: 'none',
        whiteSpace: 'normal',
      };

export const getEllipsisJssStyle = (isEllipsis: boolean): JssStyle =>
  isEllipsis
    ? {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }
    : {};

export const getDisabledCheckboxRadioTextJssStyle = (isDisabled: boolean): JssStyle =>
  isDisabled
    ? {
        color: colorUtils.grey03,
        cursor: 'default',
      }
    : {
        color: colorUtils.darkBlue02,
      };

export const getDisabledInputTextJssStyle = (isDisabled: boolean): JssStyle =>
  isDisabled
    ? {
        color: colorUtils.grey03,
        cursor: 'default',
      }
    : {
        color: colorUtils.darkBlue02,
      };

export const getInputJssStyle = (type: string): JssStyle => {
  let inputStyles = {};

  switch (type) {
    case 'date':
      inputStyles = {
        borderColor: colorUtils.green01,
      };
      break;
    case 'email':
      inputStyles = {
        borderColor: colorUtils.green02,
      };
      break;
    case 'file':
      inputStyles = {
        borderColor: colorUtils.green03,
      };
      break;
    case 'hidden':
      inputStyles = {
        borderColor: colorUtils.red01,
      };
      break;
    case 'number':
      inputStyles = {
        borderColor: colorUtils.red02,
      };
      break;
    case 'password':
      inputStyles = {
        borderColor: colorUtils.red03,
      };
      break;
    case 'search':
      inputStyles = {
        borderColor: colorUtils.yellow01,
      };
      break;
    case 'tel':
      inputStyles = {
        borderColor: colorUtils.yellow02,
      };
      break;
    case 'url':
      inputStyles = {
        borderColor: colorUtils.yellow03,
      };
      break;
    default:
      inputStyles = {
        borderColor: colorUtils.grey05,
      };
  }

  return inputStyles;
};

export const getRatioJssStyle = (ratio: string): JssStyle =>
  ratio
    ? {
        aspectRatio: ratio,
        height: '100%',
        width: '100%',
      }
    : {};

export const getObjectFitJssStyle = (objectFit: TImageObjectFit): JssStyle =>
  objectFit
    ? {
        objectFit: objectFit,
      }
    : {};

export const getImageStyleJssStyle = (imageStyle: TImageStyle): JssStyle =>
  imageStyle
    ? {
        borderRadius: imageStyle === 'circle' ? '50%' : '0',
      }
    : {};
